// Rutas y reglas de traducción de UN carrier, con la misma lectura que la página de nodo de
// Denwa SS: una lista de rutas (prefijo + descripción) y otra de traducciones DNIS OUT
// (patrón → lo que se envía). Las dos salen de asterisk.routing_rules, paginadas en el
// backend (GET /v1/routing/carriers/{carrier}/reglas): un carrier copiado de Denwa tiene
// cientos de filas y traerlas todas para filtrar en el navegador ya no escala.
import React, { useState, useEffect, useCallback } from "react";
import api from "../api";
import { useConfirm } from "./ConfirmDialog";

const POR_PAGINA = 25;

/* Una fila de routing_rules leída como traducción de Denwa:
   número exacto (largo fijo = largo del prefijo) → `5932101`;  prefijo abierto → `59321800T`. */
function esExacta(r) {
  return r.largo_min != null && r.largo_min === r.largo_max && r.largo_min === (r.prefijo || "").length;
}
function patron(r) {
  return esExacta(r) ? r.prefijo : `${r.prefijo}T`;
}
/* Lo que se envía. Si la regla quita exactamente el prefijo, se lee como en Denwa (`1800T`);
   si no, se muestra la operación tal cual. */
function enviaComo(r) {
  const p = r.prepend || "";
  if (r.strip === (r.prefijo || "").length) return esExacta(r) ? p : `${p}T`;
  return `${p}${r.strip ? ` + quita ${r.strip}` : ""}`;
}
function ejemplo(r) {
  const base = /^\d+$/.test(r.prefijo || "") ? r.prefijo : "";
  const largo = esExacta(r) ? base.length
    : (base.startsWith("5939") ? 12 : Math.max(11, base.length + 3));
  const muestra = (base + "1234567890").slice(0, largo);
  return `${muestra} → ${(r.prepend || "") + muestra.slice(r.strip || 0)}`;
}
function largoTxt(r) {
  const lo = r.largo_min, hi = r.largo_max;
  if (lo == null && hi == null) return "—";
  if (lo != null && hi != null) return lo === hi ? `${lo}` : `${lo}-${hi}`;
  return lo != null ? `≥${lo}` : `≤${hi}`;
}

const th = { padding:"8px 12px", fontSize:10, fontWeight:700, textTransform:"uppercase",
             letterSpacing:".06em", color:"var(--muted)", textAlign:"left", whiteSpace:"nowrap" };
const td = { padding:"7px 12px", borderTop:"1px solid var(--border)", fontSize:12.5 };
const mono = { fontFamily:"var(--font-mono)" };
const inp = { padding:"4px 6px", borderRadius:4, border:"1px solid var(--border)",
              background:"var(--bg-raised)", color:"var(--fg)", fontSize:12 };

function Paginador({ page, total, onPage }) {
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  if (paginas <= 1) return null;
  const nums = [];
  for (let i = Math.max(1, page - 4); i <= Math.min(paginas, page + 4); i++) nums.push(i);
  const b = (p, label, activo) => (
    <button key={label} onClick={() => onPage(p)} disabled={activo}
      style={{ ...inp, cursor: activo ? "default" : "pointer", minWidth:30,
               fontWeight: activo ? 700 : 400, background: activo ? "var(--brand)" : inp.background,
               color: activo ? "#fff" : inp.color }}>{label}</button>
  );
  return (
    <div style={{ display:"flex", gap:4, alignItems:"center", padding:"10px 12px", flexWrap:"wrap" }}>
      {page > 1 && b(1, "«", false)}
      {nums.map(n => b(n, String(n), n === page))}
      {page < paginas && b(paginas, "»", false)}
      <span style={{ marginLeft:8, fontSize:11, color:"var(--muted)" }}>
        {total} en {paginas} páginas
      </span>
    </div>
  );
}

export default function RoutingDetalle({ carrier, carrierIp, onChanged }) {
  const [vista, setVista]   = useState("rutas");
  const [q, setQ]           = useState("");
  const [qDeb, setQDeb]     = useState("");
  const [page, setPage]     = useState(1);
  const [data, setData]     = useState({ total:0, items:[], totales:{ rutas:0, traducciones:0 } });
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState(null);
  const [ok, setOk]         = useState(null);
  const [edit, setEdit]     = useState(null);   // {id, descripcion, prioridad, strip, prepend}
  const [nueva, setNueva]   = useState(null);   // alta en curso
  const [confirmar, dialogoConfirm] = useConfirm();

  useEffect(() => { const t = setTimeout(() => setQDeb(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [qDeb, vista]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get(`/v1/routing/carriers/${encodeURIComponent(carrier)}/reglas`,
        { params: { vista, q: qDeb, page, per_page: POR_PAGINA } });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "No se pudieron cargar las reglas");
    } finally { setLoading(false); }
  }, [carrier, vista, qDeb, page]);
  useEffect(() => { load(); }, [load]);

  const avisar = (t) => { setOk(t); setTimeout(() => setOk(null), 2500); };

  const guardar = async () => {
    const r = data.items.find(x => x.id === edit.id);
    setErr(null);
    try {
      const prio = parseInt(edit.prioridad, 10);
      if ((edit.descripcion || "") !== (r.descripcion || "") || prio !== r.prioridad) {
        await api.patch(`/v1/routing/rules/${r.id}/datos`,
          { descripcion: edit.descripcion || "", prioridad: prio || r.prioridad });
      }
      if (vista === "traducciones") {
        const strip = parseInt(edit.strip, 10) || 0;
        if (strip !== (r.strip || 0) || (edit.prepend || "") !== (r.prepend || "")) {
          await api.patch(`/v1/routing/rules/${r.id}/discado`, { strip, prepend: edit.prepend || "" });
        }
      }
      setEdit(null); avisar("Guardado"); load();
    } catch (e) { setErr(e.response?.data?.detail || "No se pudo guardar"); }
  };

  const borrar = async (r) => {
    const que = vista === "traducciones" ? `la traducción ${patron(r)} → ${enviaComo(r)}` : `la ruta ${r.prefijo}`;
    if (!(await confirmar(`¿Borrar ${que}${r.descripcion ? ` (${r.descripcion})` : ""}?`))) return;
    try {
      if (vista === "traducciones" && !esExacta(r)) {
        // Un patrón abierto vive en la fila de su ruta: borrar la traducción = sacarle la edición.
        await api.patch(`/v1/routing/rules/${r.id}/discado`, { strip: 0, prepend: "" });
      } else {
        await api.delete(`/v1/routing/rules/${r.id}`);
      }
      avisar("Borrado"); load(); onChanged?.();
    } catch (e) { setErr(e.response?.data?.detail || "No se pudo borrar"); }
  };

  const crear = async () => {
    setErr(null);
    try {
      if (vista === "rutas") {
        const prefijo = (nueva.prefijo || "").trim();
        if (!/^\d+$/.test(prefijo)) { setErr("Prefijo: solo dígitos (en E.164: 593…)"); return; }
        await api.post("/v1/routing/rules", {
          nombre: `${carrier} — ${prefijo}`, prefijo, carrier, carrier_ip: carrierIp,
          prioridad: parseInt(nueva.prioridad, 10) || 1, activo: true,
          descripcion: nueva.descripcion || null, strip: 0, prepend: "",
        });
      } else {
        const r = await api.post(`/v1/routing/carriers/${encodeURIComponent(carrier)}/traducciones`, {
          patron: nueva.patron, destino: nueva.destino, descripcion: nueva.descripcion || null,
        });
        if (r.data?.actualizada) avisar("La traducción quedó en la ruta existente de ese prefijo");
      }
      setNueva(null); load(); onChanged?.();
    } catch (e) { setErr(e.response?.data?.detail || "No se pudo agregar"); }
  };

  const tab = (k, label, n) => (
    <button onClick={() => { setVista(k); setEdit(null); setNueva(null); }}
      style={{ background:"none", border:"none", cursor:"pointer", padding:"8px 14px", fontSize:13,
               fontWeight: vista === k ? 700 : 500, color: vista === k ? "var(--fg)" : "var(--muted)",
               borderBottom: vista === k ? "2px solid var(--brand)" : "2px solid transparent" }}>
      {label} <span className="nv-badge-muted" style={{ fontSize:11, marginLeft:4 }}>{n}</span>
    </button>
  );

  const enEdicion = (r) => edit && edit.id === r.id;

  return (
    <div style={{ padding:"14px 16px", background:"var(--bg-surface)" }}>
      {dialogoConfirm}
      <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:6 }}>
        <strong style={{ fontSize:13 }}>Nodo :: {carrier}</strong>
        <span style={{ fontSize:11, color:"var(--muted)", ...mono }}>{carrierIp}</span>
      </div>

      <div style={{ display:"flex", alignItems:"center", gap:8, borderBottom:"1px solid var(--border)", flexWrap:"wrap" }}>
        {tab("rutas", "Rutas", data.totales?.rutas ?? 0)}
        {tab("traducciones", "Reglas de traducción", data.totales?.traducciones ?? 0)}
        <div style={{ flex:1 }}/>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar prefijo o descripción…"
          style={{ ...inp, width:240, padding:"6px 10px" }}/>
        <button className="nv-btn nv-btn-primary nv-btn-sm" style={{ marginBottom:4 }}
          onClick={() => setNueva(vista === "rutas" ? { prefijo:"", descripcion:"", prioridad:"1" }
                                                    : { patron:"", destino:"", descripcion:"" })}>
          + {vista === "rutas" ? "Nueva ruta" : "Nueva traducción"}
        </button>
      </div>

      {vista === "traducciones" && (
        <p style={{ margin:"8px 0", fontSize:11.5, color:"var(--muted)" }}>
          Igual que en Denwa: el patrón se compara contra el número <strong>ya en E.164</strong> (<code>593…</code>),
          así que da igual si el cliente marca <code>02101</code> o <code>5932101</code>.{" "}
          <code>5932101</code> = número exacto · <code>59321800T</code> = prefijo, <code>T</code> es el resto del
          número y se conserva.
        </p>
      )}

      {nueva && (
        <div className="nv-card" style={{ margin:"10px 0", padding:"10px 12px", display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap" }}>
          {vista === "rutas" ? (<>
            <label style={{ fontSize:10, color:"var(--muted)" }}>Prefijo<br/>
              <input value={nueva.prefijo} placeholder="5939600" style={{ ...inp, ...mono, width:130 }}
                onChange={e => setNueva(n => ({ ...n, prefijo: e.target.value.replace(/\D/g, "") }))}/></label>
            <label style={{ fontSize:10, color:"var(--muted)" }}>Descripción<br/>
              <input value={nueva.descripcion} placeholder="CNT" style={{ ...inp, width:200 }}
                onChange={e => setNueva(n => ({ ...n, descripcion: e.target.value }))}/></label>
            <label style={{ fontSize:10, color:"var(--muted)" }}>Prio.<br/>
              <input value={nueva.prioridad} style={{ ...inp, width:50, textAlign:"center" }}
                onChange={e => setNueva(n => ({ ...n, prioridad: e.target.value.replace(/\D/g, "").slice(0, 2) }))}/></label>
          </>) : (<>
            <label style={{ fontSize:10, color:"var(--muted)" }}>Patrón<br/>
              <input value={nueva.patron} placeholder="59321800T · 5932101" style={{ ...inp, ...mono, width:160 }}
                onChange={e => setNueva(n => ({ ...n, patron: e.target.value.toUpperCase().replace(/[^\d.T]/g, "") }))}/></label>
            <label style={{ fontSize:10, color:"var(--muted)" }}>Se envía como<br/>
              <input value={nueva.destino} placeholder="1800T · 101" style={{ ...inp, ...mono, width:160 }}
                onChange={e => setNueva(n => ({ ...n, destino: e.target.value.toUpperCase().replace(/[^\dT]/g, "") }))}/></label>
            <label style={{ fontSize:10, color:"var(--muted)" }}>Descripción<br/>
              <input value={nueva.descripcion} style={{ ...inp, width:200 }}
                onChange={e => setNueva(n => ({ ...n, descripcion: e.target.value }))}/></label>
          </>)}
          <button className="nv-btn nv-btn-primary nv-btn-sm" onClick={crear}>Agregar</button>
          <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={() => setNueva(null)}>Cancelar</button>
        </div>
      )}

      {err && <div style={{ margin:"8px 0", color:"var(--danger,#dc2626)", fontSize:12 }}>{err}</div>}
      {ok && <div style={{ margin:"8px 0", color:"var(--success,#16a34a)", fontSize:12 }}>✓ {ok}</div>}

      <div className="nv-card" style={{ padding:0, overflow:"auto", marginTop:8 }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            {vista === "rutas" ? (
              <tr><th style={th}>Prefijo</th><th style={th}>Descripción</th><th style={th}>Largo</th>
                  <th style={{ ...th, textAlign:"center" }}>Prio.</th><th style={th}>Edición</th><th style={{ ...th, textAlign:"right" }}>Acción</th></tr>
            ) : (
              <tr><th style={th}>ANI/DNIS</th><th style={th}>Tipo</th><th style={th}>Patrón</th><th style={th}>Se envía como</th>
                  <th style={th}>Descripción</th><th style={th}>Ejemplo</th><th style={{ ...th, textAlign:"right" }}>Acción</th></tr>
            )}
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ ...td, textAlign:"center", color:"var(--muted)", padding:20 }}>Cargando…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={7} style={{ ...td, textAlign:"center", color:"var(--muted)", padding:20 }}>
                {qDeb ? `Nada coincide con «${qDeb}»` : "Sin reglas"}</td></tr>
            ) : data.items.map(r => vista === "rutas" ? (
              <tr key={r.id} style={{ opacity: r.activo ? 1 : 0.5 }}>
                <td style={{ ...td, ...mono, fontWeight:600 }}>
                  {r.es_default ? "ruta por defecto" : r.es_numeracion ? "Números Netvoice" : r.prefijo}
                </td>
                <td style={td}>{enEdicion(r)
                  ? <input value={edit.descripcion} style={{ ...inp, width:"100%" }}
                      onChange={e => setEdit(x => ({ ...x, descripcion: e.target.value }))}/>
                  : (r.descripcion || <span style={{ color:"var(--muted)" }}>—</span>)}</td>
                <td style={{ ...td, ...mono }}>{largoTxt(r)}</td>
                <td style={{ ...td, textAlign:"center" }}>{enEdicion(r)
                  ? <input value={edit.prioridad} style={{ ...inp, width:44, textAlign:"center" }}
                      onChange={e => setEdit(x => ({ ...x, prioridad: e.target.value.replace(/\D/g, "").slice(0, 2) }))}/>
                  : r.prioridad}</td>
                <td style={{ ...td, ...mono, color:"var(--muted)", fontSize:11.5 }}>
                  {(r.strip || r.prepend) ? `${patron(r)} → ${enviaComo(r)}` : ""}</td>
                <td style={{ ...td, textAlign:"right", whiteSpace:"nowrap" }}>
                  {enEdicion(r) ? (<>
                    <button className="nv-btn nv-btn-primary nv-btn-sm" onClick={guardar}>Guardar</button>{" "}
                    <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={() => setEdit(null)}>✕</button>
                  </>) : (<>
                    <button className="nv-btn nv-btn-ghost nv-btn-sm"
                      onClick={() => setEdit({ id:r.id, descripcion:r.descripcion || "", prioridad:String(r.prioridad ?? "") })}>Modificar</button>{" "}
                    {!r.es_default && !r.es_numeracion &&
                      <button className="nv-btn nv-btn-ghost nv-btn-sm" style={{ color:"var(--danger)" }} onClick={() => borrar(r)}>Borrar</button>}
                  </>)}
                </td>
              </tr>
            ) : (
              <tr key={r.id}>
                <td style={td}>DNIS</td>
                <td style={td}>OUT</td>
                <td style={{ ...td, ...mono, fontWeight:600 }}>{patron(r)}</td>
                <td style={{ ...td, ...mono }}>{enEdicion(r) ? (
                  <span style={{ display:"inline-flex", gap:4, alignItems:"center", fontSize:11 }}>
                    quita <input value={edit.strip} style={{ ...inp, width:36, textAlign:"center" }}
                      onChange={e => setEdit(x => ({ ...x, strip: e.target.value.replace(/\D/g, "").slice(0, 2) }))}/>
                    antepone <input value={edit.prepend} style={{ ...inp, ...mono, width:140 }}
                      onChange={e => setEdit(x => ({ ...x, prepend: e.target.value.replace(/\D/g, "") }))}/>
                  </span>) : <span style={{ color:"var(--brand)" }}>{enviaComo(r)}</span>}</td>
                <td style={td}>{enEdicion(r)
                  ? <input value={edit.descripcion} style={{ ...inp, width:"100%" }}
                      onChange={e => setEdit(x => ({ ...x, descripcion: e.target.value }))}/>
                  : (r.descripcion || <span style={{ color:"var(--muted)" }}>—</span>)}</td>
                <td style={{ ...td, ...mono, fontSize:11, color:"var(--muted)", whiteSpace:"nowrap" }}>{ejemplo(r)}</td>
                <td style={{ ...td, textAlign:"right", whiteSpace:"nowrap" }}>
                  {enEdicion(r) ? (<>
                    <button className="nv-btn nv-btn-primary nv-btn-sm" onClick={guardar}>Guardar</button>{" "}
                    <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={() => setEdit(null)}>✕</button>
                  </>) : (<>
                    <button className="nv-btn nv-btn-ghost nv-btn-sm"
                      onClick={() => setEdit({ id:r.id, descripcion:r.descripcion || "", prioridad:String(r.prioridad ?? ""),
                                               strip:String(r.strip || ""), prepend:r.prepend || "" })}>Modificar</button>{" "}
                    <button className="nv-btn nv-btn-ghost nv-btn-sm" style={{ color:"var(--danger)" }} onClick={() => borrar(r)}>Borrar</button>
                  </>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Paginador page={page} total={data.total} onPage={setPage}/>
      </div>
    </div>
  );
}
